/**
 * MapSurface Component
 * The portalled body of a map block: the canvas or its stand-in, with the
 * location list floating over the expanded stage as a glass card rail (the
 * baked Atlas stage/Terrace chrome) or stacked beneath it in compact mode.
 *
 * The list is present on EVERY surface here. A block that parsed named real
 * places, and those names are what the reader acts on — "Berlin, Lisbon,
 * Austin" is useful even when not one of them could be plotted, and more useful
 * still once a failure carries its reason or its candidates. Only a block that
 * named nothing plottable has nothing to show, and that one never reaches this
 * component. An embedder that states the same names in its own chrome directly
 * above the block may drop the rail (`MapPinRestatement.LocationRail`) — but
 * only while the markers are drawn and carrying the names themselves; on the
 * list-only and resolving surfaces the suppression is refused, because there
 * the names have nowhere else to be.
 *
 * This is also the second of the two gates that keep WebGL contexts bounded. The
 * first is `MapCompactPreview`, which never renders a canvas. This one refuses
 * to render `MapCanvas` while the shell is in compact mode — because
 * `Cmarkdownvisual` portals its children unconditionally, so a collapsed block's
 * children ARE mounted, into a host that is still detached from the document.
 */

'use client';

import { useCallback, useMemo, useState, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { getFontFamily } from '@/theme/typography';
import { dataEl, type DataElScope } from '@/utils/dataEl';
import {
  CMARKDOWNVISUAL_MODE,
  useCmarkdownvisualMode,
} from '../Cmarkdownvisual/CmarkdownvisualModeContext';
import { MapCanvas, type MapCanvasFocus } from './MapCanvas';
import { MapLocationList, MapLocationListVariant } from './MapLocationList';
import {
  useMapPinIdleVariant,
  useMapPoiFollowUp,
  type MapPinIdleVariant,
} from './map-presentation.context';
import { MapResolvingLoader } from './MapResolvingLoader';
import { MAP_CANVAS_MIN_HEIGHT } from './map-constants';
import type { MapPoiFollowUp } from './map-poi-follow-up';
import {
  MAP_CANVAS_VISIBLE_BY_SURFACE,
  MapListNote,
  MapRenderSurface,
  type MapCanvasPoint,
  type MapLocationEntry,
  type MapRestatementChrome,
} from './map-render-model';
import type { MapPoint } from './map-style';
import './MapExpandedSurface.css';

/** Why the list is standing alone. Never phrased as the block being at fault
 *  when it was the drawing that failed, and never as a failure when nothing was
 *  ever able to run. */
const NOTE_TEXT_BY_LIST_NOTE: Record<MapListNote, string | null> = {
  [MapListNote.None]: null,
  [MapListNote.Unresolved]: 'None of these places could be located, so there is no map to draw.',
  [MapListNote.ResolutionUnavailable]:
    'These places have no positions yet, so there is no map to draw.',
  [MapListNote.CanvasUnavailable]: 'The map could not be drawn. The locations are listed below.',
};

const ROOT_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  gap: 10,
};

/** Everything about the drawn slot except its floor, which the embedder owns
 *  through `canvasMinHeight`. */
const CANVAS_SLOT_BASE_STYLE: CSSProperties = {
  display: 'flex',
  flex: 1,
};

const SKELETON_SLOT_BASE_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
};

const NOTE_STYLE: CSSProperties = {
  fontFamily: getFontFamily(),
  fontSize: 11,
  color: 'rgba(255,255,255,0.45)',
  paddingInlineStart: 2,
};

/** The baked Atlas stage/Terrace chrome: the Atlas family class carries the
 *  shared embed chrome (POI cards, the floating rail, accent details); the
 *  Terrace modifier recuts the dots + cards (teardrop pins, two-line cards). */
const EXPANDED_STAGE_CLASS_NAME =
  'map-expanded map-expanded--atlas-stage map-expanded--atlas-terrace';

/** The live pin axis, stamped on the stage for MapExpandedSurface.css to key
 *  on. Always present — a provider-less embedder stamps the shipped pick, so
 *  the CSS has exactly one addressing scheme instead of a default block plus
 *  attribute overrides that can drift apart. (The chosen treatment locked to
 *  Halo and carries no attribute.) */
const PIN_IDLE_VARIANT_ATTRIBUTE = 'data-map-pin-idle-variant';

/** The live follow-up axis, stamped beside the pin one — but omitted entirely
 *  when no follow-up is resolved, because unlike the pin axis this one has a
 *  genuine "none" state and an attribute stamped `null` would assert a pick
 *  that was never made. Only the card treatment is CSS; the other two are
 *  canvas work and live in MapCanvas. */
const POI_FOLLOW_UP_ATTRIBUTE = 'data-map-poi-follow-up';

/**
 * The stage's own floor lives in MapExpandedSurface.css, on the element this
 * variable is set on, so an inline height on the INNER canvas slot can never
 * lower it — a card-scale embed asking for 184px still got the stylesheet's
 * 320px. The stylesheet reads the variable and falls back to its own value, so
 * the floor moves only for an embedder that named a height.
 */
type MapStageStyle = CSSProperties & {
  '--map-stage-min-height': string;
};

const MAP_STAGE_MIN_HEIGHT_UNIT = 'px';

export interface MapSurfaceProps {
  dataElScope: DataElScope;
  surface: MapRenderSurface;
  note: MapListNote;
  entries: MapLocationEntry[];
  points: MapCanvasPoint[];
  zoom: number | null;
  center: MapPoint | null;
  /** Height, in pixels, for the drawn canvas and the resolving skeleton that
   *  stands in for it — and, when given, for the expanded stage that holds
   *  them. Null when the embedder named none: the slots take
   *  `MAP_CANVAS_MIN_HEIGHT` and the stage keeps its stylesheet floor. */
  canvasMinHeight: number | null;
  /** Which restatements of a pin this embed draws. Resolved by `Cmarkdownmap`
   *  from its own prop. */
  chrome: MapRestatementChrome;
  isDark: boolean;
  accessibleName: string;
  onCanvasUnavailable: () => void;
}

export function MapSurface({
  dataElScope,
  surface,
  note,
  entries,
  points,
  zoom,
  center,
  canvasMinHeight,
  chrome,
  isDark,
  accessibleName,
  onCanvasUnavailable,
}: MapSurfaceProps): ReactElement {
  const mode = useCmarkdownvisualMode();
  const isExpanded = mode !== CMARKDOWNVISUAL_MODE.Compact;
  // The live pin axis, stamped on the stage below; every other map design
  // axis is retired and baked into MapExpandedSurface.css.
  const pinIdleVariant = useMapPinIdleVariant();
  const poiFollowUp = useMapPoiFollowUp();
  // The chosen place, from EITHER end: a rail card names its pin, a pin names
  // its rail card. The card path additionally asks the canvas to fly (the pin
  // may be off screen); a pin picked on the map is already under the pointer,
  // so it selects without moving the camera.
  const [activeKey, setActiveKey] = useState<string | null>(null);
  // The card-click flight request. The nonce re-fires a repeat click on the
  // same row.
  const [focus, setFocus] = useState<MapCanvasFocus | null>(null);
  const handleEntryFocus = useCallback((entry: MapLocationEntry): void => {
    const position = entry.position;
    if (!position) {
      return;
    }
    setActiveKey(entry.key);
    setFocus((previous) => ({
      key: entry.key,
      lat: position.lat,
      lon: position.lon,
      nonce: (previous?.nonce ?? 0) + 1,
    }));
  }, []);
  const handleMarkerPick = useCallback((key: string): void => {
    setActiveKey(key);
  }, []);
  const showCanvas = MAP_CANVAS_VISIBLE_BY_SURFACE[surface] && isExpanded;
  const showSkeleton = surface === MapRenderSurface.Resolving && isExpanded;
  const noteText = NOTE_TEXT_BY_LIST_NOTE[note];
  const slotMinHeight = canvasMinHeight ?? MAP_CANVAS_MIN_HEIGHT;
  const canvasSlotStyle = useMemo<CSSProperties>(
    () => ({ ...CANVAS_SLOT_BASE_STYLE, minHeight: slotMinHeight }),
    [slotMinHeight],
  );
  const skeletonSlotStyle = useMemo<CSSProperties>(
    () => ({ ...SKELETON_SLOT_BASE_STYLE, minHeight: slotMinHeight }),
    [slotMinHeight],
  );
  const stageStyle = useMemo<MapStageStyle | undefined>(
    () =>
      canvasMinHeight == null
        ? undefined
        : {
            '--map-stage-min-height': `${String(canvasMinHeight)}${MAP_STAGE_MIN_HEIGHT_UNIT}`,
          },
    [canvasMinHeight],
  );

  // The canvas is the one part of this component that runs a third-party
  // renderer against a GPU, so it is also the one part whose runtime errors are
  // not this codebase's to predict. Without a boundary of its own the nearest
  // one is the app's, and a throw from inside MapLibre replaces the WHOLE page
  // — on 2026-08-23 a browser with no WebGL2 took the pillar page and its tab
  // strip down that way, leaving nothing to navigate with. The slot keeps its
  // reserved height and renders empty instead; the location list below is
  // rendered by this component, outside the boundary, so the places survive.
  const mapSlot = showCanvas ? (
    <div data-el={dataEl(dataElScope, 'MapSurface/CanvasViewport')} style={canvasSlotStyle}>
      <ErrorBoundary fallback={null}>
        <MapCanvas
          dataElScope={dataElScope}
          points={points}
          zoom={zoom}
          center={center}
          isDark={isDark}
          accessibleName={accessibleName}
          focus={focus}
          chosenKey={activeKey}
          onMarkerPick={handleMarkerPick}
          onUnavailable={onCanvasUnavailable}
        />
      </ErrorBoundary>
    </div>
  ) : null;

  const skeletonSlot = showSkeleton ? (
    <div
      style={skeletonSlotStyle}
    >
      <MapResolvingLoader
        dataElScope={dataElScope}
        entries={entries}
        accessibleName={accessibleName}
      />
    </div>
  ) : null;

  // The baked Globe orbit loader carries the names itself; a second copy below
  // it would be the exact "ugly list" the treatment exists to replace. Rows
  // become focus buttons only where a canvas exists to fly.
  const canFocusRows = showCanvas;
  // An embedder may drop the rail only where the drawn markers carry the names
  // themselves. On a list-only surface the rail IS the block — the whole point
  // of this component is that a block which parsed never loses its place names
  // — so the opt-in is gated on the canvas actually being on screen.
  const isRailSuppressed = !chrome.showLocationRail && showCanvas;
  const listSlot =
    showSkeleton || isRailSuppressed ? null : (
      <MapLocationList
        dataElScope={dataElScope}
        entries={entries}
        variant={MapLocationListVariant.Canvas}
        showCoordinateDetail={chrome.showCoordinateDetail}
        onEntryClick={canFocusRows ? handleEntryFocus : undefined}
        activeEntryKey={canFocusRows ? activeKey : null}
      />
    );

  return (
      <div
        data-el={dataEl(dataElScope, 'MapSurface/Root')}
        style={ROOT_STYLE}
      >
        {isExpanded ? (
          renderExpandedChrome({
            dataElScope,
            mapContent: (
              <>
                {mapSlot}
                {skeletonSlot}
              </>
            ),
            listContent: listSlot,
            stageStyle,
            pinIdleVariant,
            poiFollowUp,
            showListOverlay: !isRailSuppressed,
          })
        ) : (
          <>
            {mapSlot}
            {skeletonSlot}
            {listSlot}
          </>
        )}
        {noteText ? (
          <div data-el={dataEl(dataElScope, 'MapSurface/Status')} style={NOTE_STYLE}>
            {noteText}
          </div>
        ) : null}
      </div>
  );
}

interface MapExpandedChromeInput {
  dataElScope: DataElScope;
  mapContent: ReactNode;
  listContent: ReactNode;
  /** Carries the embedder's canvas height to the stage floor, or undefined
   *  when none was named and the stylesheet's own floor stands. */
  stageStyle: MapStageStyle | undefined;
  /** The live pin axis, stamped as a stage attribute for the CSS. */
  pinIdleVariant: MapPinIdleVariant;
  /** The live follow-up axis, or null when none is resolved. */
  poiFollowUp: MapPoiFollowUp | null;
  /** False only where the embedder suppressed the rail. The overlay owns the
   *  rail's scrim, so it has to go with it — an empty overlay still paints a
   *  gradient band across the foot of the map. */
  showListOverlay: boolean;
}

function renderExpandedChrome({
  dataElScope,
  mapContent,
  listContent,
  stageStyle,
  pinIdleVariant,
  poiFollowUp,
  showListOverlay,
}: MapExpandedChromeInput): ReactElement {
  // The map owns the WHOLE stage; the card rail floats over its lower area as
  // an absolutely-positioned overlay that reserves no layout height at all
  // (geometry in MapExpandedSurface.css). The stage classes plus the pin
  // attribute are the only hooks the CSS needs — always present, so they
  // carry the card material and the shared selected-expand contract alike.
  return (
    <div
      className={EXPANDED_STAGE_CLASS_NAME}
      {...{
        [PIN_IDLE_VARIANT_ATTRIBUTE]: pinIdleVariant,
        [POI_FOLLOW_UP_ATTRIBUTE]: poiFollowUp ?? undefined,
      }}
    >
      <div className="map-expanded__map-slot" style={stageStyle}>
        {mapContent}
      </div>
      {showListOverlay ? (
        <div data-el={dataEl(dataElScope, 'MapSurface/Rail')} className="map-expanded__list-overlay">
          <div className="map-expanded__list-slot">{listContent}</div>
        </div>
      ) : null}
    </div>
  );
}
